import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Suspense, useEffect, type JSX } from 'react';
import { useAuth } from '@/context/AuthContext';
import { tokenManager } from '@/auth';
import { MainLayout } from '@/layouts/MainLayout';
import NotFoundPage from '@gokrew/ui/pages/NotFound';
import NoAccessPage from '@gokrew/ui/pages/NoAccessPage';
import { AppLoaderPage } from '@gokrew/ui/components/AppLoaderPage';
import { AppRouter } from '@gokrew/ui/components/AppRouter';
import {
  authRoutes,
  mainLayoutRoutes,
  onboardingRoutes,
  type ChildRoute,
} from './config';
import { usePermissions } from '@/context/PermissionContext';
import { SelectWorkspacePage } from '@/pages/auth-v2/pages/SelectWorkspacePage';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useRouteWithPermission } from '@/hooks/useRouteWithPermission';
import { useRouteDocumentTitle } from '@/hooks/useRouteDocumentTitle';
import { PermissionRouterGuard } from '@/context/PermissionRouterGuard';
import { SlackCallbackPage } from '@/pages/slack/SlackCallbackPage';
import { LoaderProvider } from '@/context/LoaderProvider';
import AdminSlackLinkPage from '@/pages/slack/SlackLinkPage';
import WorkspaceLinkPage from '@/pages/slack/WorkspaceLinkPage';
import AdminTeamsLinkPage from '@/pages/teams/TeamsLinkPage';
import TeamsConnectPage from '@/pages/teams/TeamsConnectPage';
import TeamsCallbackPage from '@/pages/teams/TeamsCallbackPage';
import { TEAMS_TOKEN_KEY } from '@/pages/teams/teams-token-key';
import { MockLoginPage } from '@/pages/auth/MockLoginPage';
import { MockSessionExpiredPage } from '@/pages/auth/MockSessionExpiredPage';
import { OnboardingRoute } from '@gokrew/models/onboarding';
import { McpConsentPage } from '@/pages/mcp-consent/McpConsentPage';
import { getConfig } from '@gokrew/ui/runtime-config';
import { rememberTeamsPopup } from '@gokrew/ui/utility/teamsPopupReturn';

function isSafeInternalRedirect(redirect: string | null): redirect is string {
  return (
    !!redirect &&
    redirect.startsWith('/') &&
    !redirect.startsWith('//') &&
    !redirect.includes('\\')
  );
}

/**
 * Public, auth-independent routes that must render immediately without waiting
 * for silent session restore — otherwise the full-screen AppLoaderPage flashes
 * during `isInitializing` on a fresh load (e.g. after ending a mock session).
 */
const PUBLIC_STANDALONE_PATHS = ['/mock-session-expired', '/mock-login'];

/**
 * Recursively renders a route's `children` config, guarding each non-index
 * entry behind its own permission scope, and recursing into any further
 * nested `children` (e.g. a tab route that itself has a detail sub-route).
 */
function renderChildRoutes(children: ChildRoute[] | undefined) {
  return children?.map((child, index) =>
    child.index ? (
      <Route key={`index-${index}`} index element={child.element} />
    ) : (
      <Route
        key={child.path}
        path={child.path}
        element={
          <PermissionGuard scope={child.scope} fallback={<NoPermissionPage />}>
            {child.element}
          </PermissionGuard>
        }
      >
        {renderChildRoutes(child.children)}
      </Route>
    )
  );
}

/**
 * AppRoutes is a custom route that checks if the user is logged in
 * and if the user has a company. If the user is not logged in, it will
 * redirect to the login page. If the user is logged in but does not have
 * a company, it will redirect into the onboarding flow.
 */
export function AppRoutes() {
  const { token, isInitializing } = useAuth();
  const isPublicStandalone = PUBLIC_STANDALONE_PATHS.includes(
    window.location.pathname
  );

  if (isInitializing && !isPublicStandalone) return <AppLoaderPage />;

  return (
    <AppRouter>
      <Suspense fallback={null}>
        <RouteDocumentTitle />
      </Suspense>
      <LoaderProvider>
        <PermissionRouterGuard>
          <Suspense fallback={<AppLoaderPage />}>
            <Routes>
              <Route path="/" element={<Navigate to="/login" />} />
              {authRoutes.map(({ path, element }) => (
                <Route
                  key={path}
                  path={path}
                  element={<RedirectIfAuthenticated page={element} />}
                />
              ))}

              {/* Onboarding flow — token-guarded; unauthenticated users are
                  sent to signup. Shared sidebar layout + step content. */}
              <Route
                path={onboardingRoutes.path}
                element={
                  token ? (
                    onboardingRoutes.layout
                  ) : (
                    <Navigate to="/signup" replace />
                  )
                }
              >
                <Route index element={<Navigate to="company" replace />} />
                {onboardingRoutes.children.map((child) => (
                  <Route
                    key={child.path}
                    path={child.path}
                    element={child.element}
                  />
                ))}
              </Route>
              {/* Deprecated: company creation now lives in the onboarding
                  flow. Kept so stale links and bookmarks keep working. */}
              <Route
                path="/add-company"
                element={<Navigate to={OnboardingRoute.Company} replace />}
              />
              {/* Deprecated alias — kept so stale links keep working. */}
              <Route
                path="/signup/company"
                element={<Navigate to={OnboardingRoute.Company} replace />}
              />
              <Route
                path="/signup/workspace"
                element={
                  token ? (
                    <SelectWorkspacePage />
                  ) : (
                    <Navigate to="/signup" replace />
                  )
                }
              />
              <Route path="/slack/callback" element={<SlackCallbackPage />} />
              <Route path="/mock-login" element={<MockLoginPage />} />
              <Route
                path="/mock-session-expired"
                element={<MockSessionExpiredPage />}
              />

              {/* Slack admin account linking ← new */}
              <Route
                path="/slack/link"
                element={
                  <SlackAuthRoute redirectPath="/slack/link">
                    <AdminSlackLinkPage />
                  </SlackAuthRoute>
                }
              />

              {/* Slack workspace-to-company mapping (marketplace/direct install) */}
              <Route
                path="/slack/link-workspace"
                element={
                  <SlackAuthRoute redirectPath="/slack/link-workspace">
                    <WorkspaceLinkPage />
                  </SlackAuthRoute>
                }
              />

              {/* Teams tenant binding — opened from the bot's connect card */}
              <Route
                path="/teams/connect"
                element={
                  <SlackAuthRoute
                    tokenKey={TEAMS_TOKEN_KEY}
                    redirectPath="/teams/connect"
                  >
                    <TeamsConnectPage />
                  </SlackAuthRoute>
                }
              />

              {/* MCP OAuth consent screen — /authorize redirects here the
                  first time a user encounters a given MCP client. Deliberately
                  not under /mcp or /authorize — both prefixes are proxied
                  straight to the backend (dev proxy + prod reverse proxy), so
                  a route living there would never reach the SPA.
                  Gated behind ENABLE_MCP_CONNECTOR — set in .env.staging,
                  deliberately omitted from .env.production (same pattern as
                  ENABLE_TOUR) so this stays off in prod while the backend/
                  Caddy side of the connector ships everywhere. */}
              {getConfig().ENABLE_MCP_CONNECTOR && (
                <Route
                  path="/oauth/consent"
                  element={
                    <McpConsentRoute>
                      <McpConsentPage />
                    </McpConsentRoute>
                  }
                />
              )}

              {/* Teams admin account linking */}
              <Route
                path="/teams/link"
                element={
                  <SlackAuthRoute
                    tokenKey={TEAMS_TOKEN_KEY}
                    redirectPath="/teams/link"
                  >
                    <AdminTeamsLinkPage />
                  </SlackAuthRoute>
                }
              />

              {/* Where the backend's Microsoft sign-in callback lands */}
              <Route
                path="/teams/callback"
                element={
                  <SlackAuthRoute redirectPath="/teams/callback">
                    <TeamsCallbackPage />
                  </SlackAuthRoute>
                }
              />

              {/* Private authenticated routes */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <MainLayout />
                  </PrivateRoute>
                }
              >
                {mainLayoutRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <PermissionGuard
                        scope={route.scope}
                        fallback={route.fallbackElement || <NoPermissionPage />}
                      >
                        {route.element}
                      </PermissionGuard>
                    }
                  >
                    {renderChildRoutes(route.children)}
                  </Route>
                ))}
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </PermissionRouterGuard>
      </LoaderProvider>
    </AppRouter>
  );
}

// -----------------------------------------------------------------------------------------------------
// @ Local Components
// -----------------------------------------------------------------------------------------------------

/** Keeps the browser tab title in step with the route and language. */
function RouteDocumentTitle() {
  useRouteDocumentTitle();
  return null;
}

/**
 * PrivateRoute is a custom route that checks if the user is logged in
 * and if the user has a company. If the user is not logged in, it will
 * redirect to the login page. If the user is logged in but does not have
 * a company, it will redirect into the onboarding flow.
 */
function PrivateRoute({ children }: { children: React.JSX.Element }) {
  const { fetchingPermissions } = usePermissions();
  const { token, user, hasCompany, isLoading } = useAuth();
  const location = useLocation();

  // Keep where they were going (an email or notification link), query and
  // all, so the login page can send them back there instead of the dashboard.
  if (!token) {
    const redirect = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirect)}`}
        replace
      />
    );
  }

  // wait until hasCompany is determined
  if (hasCompany === null || isLoading || fetchingPermissions) {
    return <AppLoaderPage />;
  }

  if (user && !hasCompany) {
    return <Navigate to={OnboardingRoute.Company} replace />;
  }

  return children;
}

/**
 * A page that is shown when the user does not have permission to view a particular page.
 * It displays a warning icon, a heading, and a subheading.
 */
function NoPermissionPage() {
  const routeWithPermission = useRouteWithPermission();
  return <NoAccessPage redirectTo={routeWithPermission} />;
}

/**
 * Silently re-issues the mcp_bridge_token cookie for a user who is already
 * authenticated in this browser, then hands them back to the MCP OAuth
 * `/authorize` request — no re-entering credentials required.
 */
function McpBridgeHandoff() {
  const location = useLocation();

  useEffect(() => {
    const redirect = new URLSearchParams(location.search).get('redirect');
    const isSafeRedirect = isSafeInternalRedirect(redirect);

    tokenManager.forceRefresh().finally(() => {
      window.location.replace(isSafeRedirect ? redirect : '/dashboard');
    });
  }, [location.search]);

  return <AppLoaderPage />;
}

/**
 * Redirects to the page if the user has permission to view a particular page.
 */
function RedirectIfAuthenticated({ page }: { page: React.JSX.Element }) {
  const { fetchingPermissions } = usePermissions();
  const routeWithPermission = useRouteWithPermission();
  const { token, isLoading, hasCompany, user } = useAuth();
  const location = useLocation();

  // The MCP OAuth bridge needs a fresh access token issued (to re-set the
  // mcp_bridge_token cookie), even for a user already authenticated in this
  // browser — but an already-authenticated user should get that silently
  // instead of being forced to type their password again.
  const isMcpAuthMode =
    new URLSearchParams(location.search).get('authMode') === 'mcp';

  if (isMcpAuthMode && token && user && !isLoading && !fetchingPermissions) {
    return <McpBridgeHandoff />;
  }

  if (
    !token ||
    !user ||
    isLoading ||
    hasCompany === null ||
    fetchingPermissions ||
    isMcpAuthMode
  ) {
    return page;
  }
  if (!hasCompany) return <Navigate to={OnboardingRoute.Company} replace />;
  return <Navigate to={routeWithPermission} replace />;
}

/**
 * Protects /oauth/consent. If not logged in, sends the user through the
 * normal login flow first, preserving the full OAuth consent request
 * (client_id, redirect_uri, code_challenge, etc.) as the `redirect` target
 * so they land back on this exact consent screen once authenticated.
 */
function McpConsentRoute({ children }: { children: JSX.Element }) {
  const { token } = useAuth();
  const location = useLocation();

  if (!token) {
    const redirect = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirect)}`}
        replace
      />
    );
  }

  return children;
}

/**
 * Protects /slack/link. If not logged in, stashes the Slack token from the
 * URL fragment into sessionStorage so it survives the login redirect, then
 * sends the user to /login.
 */
function SlackAuthRoute({
  children,
  redirectPath,
  tokenKey = 'slack_link_token',
}: {
  children: JSX.Element;
  redirectPath: string;
  /** Must differ per provider so two link flows cannot clobber each other. */
  tokenKey?: string;
}) {
  const { token } = useAuth();
  const location = useLocation();

  if (!token) {
    const hash = location.hash;
    const match = hash.match(/[#&]token=([^&]*)/);
    if (match) {
      sessionStorage.setItem(tokenKey, decodeURIComponent(match[1]));
    }
    rememberTeamsPopup();
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
        replace
      />
    );
  }

  return children;
}
